import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Box, MenuItem, TextField } from '@mui/material'

import CustomDialog from 'common/components/CustomDialog'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { useMutateCreateProduct, useMutateEditProduct, useMutateUploadImage } from 'modules/inventory/api/mutation'
import { useProductCategory, useProductV3ById, useUnits } from 'modules/inventory/api/queries'
import { ProductBody, Units } from 'modules/inventory/api/type'
import SingleImageUpload from 'modules/inventory/components/ImageDropZone'

interface CreateProductDialogProps {
  open: boolean
  onClose: () => void
  productId?: string
}

const CreateProductDialog = ({ open, onClose, productId }: CreateProductDialogProps) => {
  const { t } = useTranslation('product')
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const isEdit = Boolean(productId)

  const { data: existingProduct } = useProductV3ById(productId)
  const { data: units, isLoading: isUnitsLoading, isError: isUnitsError } = useUnits()
  const {
    data: categories,
    isLoading: isCatsLoading,
    isError: isCatsError,
  } = useProductCategory()

  const uploadPhotoMutation = useMutateUploadImage()
  const createProductMutation = useMutateCreateProduct()
  const editProductMutation = useMutateEditProduct()

  const schema = Yup.object().shape({
    name: Yup.string().required(t('please.input.product.name')),
    category_id: Yup.string().required(t('please.input.product.product_id')),
  })

  const resolver = useYupValidationResolver(schema)

  const { control, handleSubmit, reset, setValue, getValues } = useForm<ProductBody>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      is_active: true,
      unit_id: 0,
      category_id: '',
      image_url: '',
    },
    resolver,
  })

  // Pre-fill form when editing
  useEffect(() => {
    if (open && isEdit && existingProduct) {
      reset({
        name: existingProduct.product_name,
        is_active: true,
        unit_id: existingProduct.unit_id,
        category_id: existingProduct.category_id,
        image_url: existingProduct.product_img_url ?? '',
      })
    }
  }, [open, isEdit, existingProduct, reset])

  // Set defaults for create mode only
  useEffect(() => {
    if (!isEdit && !isUnitsLoading && units?.length && !getValues('unit_id')) {
      setValue('unit_id', units[0].id)
    }
  }, [isUnitsLoading, units, getValues, setValue, isEdit])

  useEffect(() => {
    if (!isEdit && !isCatsLoading && categories?.length && !getValues('category_id')) {
      setValue('category_id', categories[0].id)
    }
  }, [isCatsLoading, categories, getValues, setValue, isEdit])

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = async (data: ProductBody) => {
    setIsSubmitLoading(true)
    try {
      let imgUrl = ''
      const img = data.image_url
      if (img && typeof img !== 'string') {
        const fd = new FormData()
        fd.append('image', img)
        const { data: res } = await uploadPhotoMutation.mutateAsync(fd)
        imgUrl = res.data.image_url
      } else {
        imgUrl = (data.image_url as string) ?? ''
      }

      const body: ProductBody = {
        name: data.name,
        is_active: true,
        unit_id: data.unit_id,
        category_id: data.category_id,
        image_url: imgUrl,
      }

      if (isEdit && productId) {
        await editProductMutation.mutateAsync({ body, productId })
      } else {
        await createProductMutation.mutateAsync(body)
      }
      handleClose()
    } catch (e) {
      console.error('Error saving product', e)
    } finally {
      setIsSubmitLoading(false)
    }
  }

  const existingImageUrl =
    isEdit && existingProduct?.product_img_url ? existingProduct.product_img_url : null

  return (
    <CustomDialog
      open={open}
      closeDialog={handleClose}
      title={isEdit ? t('product.edit') : t('inventory.add_product')}
      submitLabel={t('product.save')}
      formName="create-product-form"
      isSubmitLoading={isSubmitLoading}
      onSubmit={() => {}}
      enableCloseButton
      enableInLineWithTitle
      maxWidth="sm"
      fullWidth
      contentMinWidth={0}
    >
      <form id="create-product-form" onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Controller
            name="image_url"
            control={control}
            render={({ field, fieldState }) => (
              <SingleImageUpload
                initialUrl={existingImageUrl}
                onChange={(file) => field.onChange(file)}
                errorText={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <label style={{ fontWeight: 600 }}>
            <span style={{ color: 'red', marginRight: 4 }}>*</span>
            {t('product.name')}
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                sx={{ mt: 0.5 }}
                placeholder={t('product.input.name')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <label style={{ fontWeight: 600 }}>
            <span style={{ color: 'red', marginRight: 4 }}>*</span>
            {t('product.category')}
          </label>
          <Controller
            name="category_id"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                sx={{ mt: 0.5 }}
                disabled={isCatsLoading || isCatsError}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              >
                {(categories ?? []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>

        <Box sx={{ mb: 1 }}>
          <label style={{ fontWeight: 600 }}>
            <span style={{ color: 'red', marginRight: 4 }}>*</span>
            {t('product.unit')}
          </label>
          <Controller
            name="unit_id"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                sx={{ mt: 0.5 }}
                onChange={(e) => field.onChange(Number(e.target.value))}
                disabled={isUnitsLoading || isUnitsError}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              >
                {(units ?? []).map((u: Units) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>
      </form>
    </CustomDialog>
  )
}

export default CreateProductDialog
